import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';
import { apiService } from '../../services/api';
import { ProfileResponse } from '../../types/profile';
import { isInterviewerRole } from '../../utils/role';
import { getFullImageUrl } from '../../utils/imageUtils';
import { Button } from '../../components/ui/Button';

export function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('No token found');
      }

      const profileData = await apiService.profile.getProfile();
      setProfile(profileData);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load profile:', err);
      console.error('Error details:', {
        status: err?.response?.status,
        message: err?.message,
        data: err?.response?.data,
      });
      setError('Failed to load profile information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-stitch-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-stitch-lg border border-stitch-danger/40 bg-stitch-danger-soft p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-stitch-danger" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-stitch-fg">Error</h3>
            <p className="mt-1 text-sm text-stitch-fg-soft">{error}</p>
          </div>
        </div>
        <div className="mt-4">
          <Button variant="danger" size="sm" onClick={loadProfile}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="rounded-stitch-lg border border-stitch-warning/40 bg-stitch-warning-soft p-6">
        <p className="text-stitch-fg">No profile information available.</p>
        <Button variant="accent" size="sm" className="mt-4" onClick={() => navigate('/profile/setup')}>
          Set Up Profile
        </Button>
      </div>
    );
  }

  const isInterviewer = isInterviewerRole(profile.role);
  const profileImageUrl = getFullImageUrl(profile.profilePictureUrl);

  return (
    <main className="mx-auto max-w-7xl space-y-8 px-4 pb-16 pt-6 sm:px-6 lg:px-8">
      <div className="overflow-hidden rounded-stitch-2xl border border-stitch-border bg-stitch-surface shadow-stitch-pop">
        <div className="relative overflow-hidden bg-gradient-to-br from-stitch-primary via-stitch-primary-hover to-stitch-accent px-6 py-10 sm:px-10 sm:py-12">
          {/* layered depth: glows + subtle dot texture */}
          <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-stitch-accent/30 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:18px_18px]" />

          <div className="relative flex flex-col items-center gap-7 text-center sm:flex-row sm:items-center sm:gap-8 sm:text-left">
            <div className="relative flex-shrink-0">
              <div className="h-28 w-28 overflow-hidden rounded-full border-4 border-white/90 bg-stitch-surface shadow-stitch-modal sm:h-32 sm:w-32">
                {profileImageUrl && !imageError ? (
                  <img
                    src={profileImageUrl}
                    alt="Profile"
                    className="h-full w-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-stitch-surface-2 text-stitch-muted">
                    <span className="material-symbols-outlined text-5xl text-stitch-muted">person</span>
                  </div>
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-stitch-surface shadow-stitch-card">
                <span className="material-symbols-outlined text-lg text-stitch-primary">verified</span>
              </div>
            </div>

            <div className="min-w-0 flex-1 text-white">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white ring-1 ring-inset ring-white/25 backdrop-blur-sm">
                <span
                  className="material-symbols-outlined text-sm"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  {isInterviewer ? 'workspace_premium' : 'school'}
                </span>
                {isInterviewer ? 'Interviewer' : 'Interviewee'}
              </span>

              <h1 className="mt-3 break-words text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
                {profile.profileName || profile.email}
              </h1>

              {profile.specialty && (
                <p className="mt-1.5 flex items-center justify-center gap-1.5 text-base font-medium text-white/90 sm:justify-start">
                  <span className="material-symbols-outlined text-lg">work</span>
                  {profile.specialty}
                </p>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-white/80 sm:justify-start">
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <span className="material-symbols-outlined text-base">mail</span>
                  <span className="truncate">{profile.email}</span>
                </span>
                {profile.country && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-base">location_on</span>
                    {profile.country}
                  </span>
                )}
              </div>

              <div className="mt-4 flex flex-wrap justify-center gap-2 sm:justify-start">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                    profile.isProfileComplete
                      ? 'bg-stitch-success-soft text-stitch-success'
                      : 'bg-stitch-warning-soft text-stitch-warning'
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">
                    {profile.isProfileComplete ? 'check_circle' : 'pending'}
                  </span>
                  {profile.isProfileComplete ? 'Ready for interviews' : 'Complete your profile'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card">
          <h2 className="mb-6 flex items-center gap-2 text-lg font-bold text-stitch-fg">
            <span className="flex h-10 w-10 items-center justify-center rounded-stitch-lg bg-stitch-primary-soft text-stitch-primary">
              <span className="material-symbols-outlined">call</span>
            </span>
            Contact Information
          </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-stitch-muted">Email</label>
            <p className="mt-1 break-words text-sm text-stitch-fg">{profile.email}</p>
          </div>
          {profile.mobile && (
            <div>
              <label className="text-sm font-medium text-stitch-muted">Mobile</label>
              <p className="mt-1 text-sm text-stitch-fg">{profile.mobile}</p>
            </div>
          )}
          {profile.profileName && (
            <div>
              <label className="text-sm font-medium text-stitch-muted">Profile Name</label>
              <p className="mt-1 text-sm text-stitch-fg">{profile.profileName}</p>
            </div>
          )}
          {profile.country && (
            <div>
              <label className="text-sm font-medium text-stitch-muted">Country</label>
              <p className="mt-1 text-sm text-stitch-fg">{profile.country}</p>
            </div>
          )}
          {profile.linkedinUrl && (
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-stitch-muted">LinkedIn</label>
              <p className="mt-1 text-sm">
                <a
                  href={profile.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-stitch-primary underline hover:text-stitch-primary-hover"
                >
                  {profile.linkedinUrl}
                </a>
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-stitch-fg">
          <span className="flex h-10 w-10 items-center justify-center rounded-stitch-lg bg-stitch-accent-soft text-stitch-accent">
            <span className="material-symbols-outlined">business_center</span>
          </span>
          Professional Information
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {profile.company && (
            <div>
              <label className="text-sm font-medium text-stitch-muted">Company</label>
              <p className="mt-1 text-sm text-stitch-fg">{profile.company}</p>
            </div>
          )}
          {profile.experience && (
            <div>
              <label className="text-sm font-medium text-stitch-muted">Experience</label>
              <p className="mt-1 text-sm text-stitch-fg">{profile.experience}</p>
            </div>
          )}
          {profile.specialty && (
            <div>
              <label className="text-sm font-medium text-stitch-muted">Specialty</label>
              <p className="mt-1 text-sm text-stitch-fg">{profile.specialty}</p>
            </div>
          )}
          {profile.purpose && (
            <div>
              <label className="text-sm font-medium text-stitch-muted">Purpose</label>
              <p className="mt-1 text-sm text-stitch-fg">{profile.purpose}</p>
            </div>
          )}
        </div>

        {profile.languages && profile.languages.length > 0 && (
          <div className="mt-6">
            <label className="mb-2 block text-sm font-medium text-stitch-muted">Languages</label>
            <div className="flex flex-wrap gap-2">
              {profile.languages.map((language, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-full bg-stitch-primary-soft px-3 py-1 text-sm font-medium text-stitch-primary"
                >
                  {language}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-stitch-fg">
          <span className="flex h-10 w-10 items-center justify-center rounded-stitch-lg bg-stitch-success-soft text-stitch-success">
            <span className="material-symbols-outlined">task_alt</span>
          </span>
          Profile Status
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-stitch-lg bg-stitch-surface-2 p-4">
            <div>
              <p className="font-medium text-stitch-fg">Profile Completion</p>
              <p className="text-sm text-stitch-muted">
                {profile.isProfileComplete
                  ? 'Your profile is complete and ready for interviews'
                  : 'Complete your profile to start booking interviews'}
              </p>
            </div>
            <div
              className={`rounded-full px-3 py-1 text-sm font-medium ${
                profile.isProfileComplete
                  ? 'bg-stitch-success-soft text-stitch-success'
                  : 'bg-stitch-warning-soft text-stitch-warning'
              }`}
            >
              {profile.isProfileComplete ? 'Complete' : 'Incomplete'}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-stitch-fg">
          <span className="material-symbols-outlined text-stitch-primary">bolt</span>
          Actions
        </h2>
        <div className="flex flex-wrap gap-4">
          <Button variant="primary" onClick={() => navigate('/profile/setup')}>
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit Profile
          </Button>

          {!profile.isProfileComplete && (
            <Button variant="accent" onClick={() => navigate('/profile/setup')}>
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Complete Setup
            </Button>
          )}

          {isInterviewer && (
            <>
              <Button variant="secondary" onClick={() => navigate('/bookings')}>
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3a4 4 0 118 0v4m-4 8a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                Manage Bookings
              </Button>

              <Button variant="secondary" onClick={() => navigate('/time-slots')}>
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Manage Time Slots
              </Button>
            </>
          )}

          {!isInterviewer && (
            <>
              <Button variant="secondary" onClick={() => navigate('/interviewers')}>
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                Find Interviewers
              </Button>

              <Button variant="secondary" onClick={() => navigate('/bookings')}>
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3a4 4 0 118 0v4m-4 8a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                My Bookings
              </Button>
            </>
          )}

          <Button
            variant="danger"
            onClick={() => {
              if (window.confirm('Are you sure you want to logout?')) {
                authService.logout();
                navigate('/login');
              }
            }}
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Logout
          </Button>
        </div>
      </section>
    </main>
  );
}
